import React, { useState, useEffect } from 'react';

import MeshScatterChart from 'components/graph/MeshScatterChart';
import { useSelector } from 'react-redux';
import { Loader } from 'screens/analytics/commonComponents';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { StyledBox, StyledPaper, StyledTypography } from 'styles/common';

import { RATINGS_TYPES, REVIEW_CYCLE_RATING_SPLIT_COLOR_MAPPING } from 'constants/ProjectConstants';
import { objectFlip } from 'constants/helper';
import { ReviewChartMiniIcon } from 'constants/icons';
import theme from 'constants/theme';

import { getPerformanceReviewData } from 'services/analytics/review';

import SectionHeader from './common/SectionHeader';

const PerformanceReviewGraphPlaceholder = () => (
  <StyledBox paddingBottom="2rem" paddingLeft="2rem" height="100%">
    <StyledBox
      position="absolute"
      left="2rem"
      bottom="3rem"
      style={{
        transform: 'rotate(-90deg)',
        transformOrigin: 'top left',
      }}
    >
      <StyledTypography variant="body1">Competency proficiency</StyledTypography>
    </StyledBox>
    <StyledBox position="absolute" left="4rem" bottom="1rem">
      <StyledTypography variant="body1">Goal achievement</StyledTypography>
    </StyledBox>
    <StyledBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100%"
      borderLeft={`2px solid ${theme.palette.borders.lightGrey}`}
      borderBottom={`2px solid ${theme.palette.borders.lightGrey}`}
    >
      <StyledTypography variant="h6" color="textSecondary" align="center" style={{ width: '60%' }}>
        Watch this space to know your growth from one review cycle to another
      </StyledTypography>
    </StyledBox>
  </StyledBox>
);

const PerformanceReviewGraph = () => {
  const [performanceReviewData, setPerformanceReviewData] = useState({
    ratingMap: {},
    reviewRatingDetailsResponseList: [],
    dataMap: RATINGS_TYPES,
    color: '',
    colorMap: REVIEW_CYCLE_RATING_SPLIT_COLOR_MAPPING,
  });
  const [loading, setLoading] = useState(true);

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  useEffect(() => {
    const fetchPerformanceReviewData = () => {
      setLoading(true);
      getPerformanceReviewData({ empId: loggedInEmployeeId })
        .then((res) => {
          const flippedRatingMap = objectFlip(res.ratingMap);
          const newReviewRatingDetailsResponseList = res.reviewRatingDetailsResponseList.map((response) => {
            const overallRating = Number.isNaN(response.overallRating)
              ? response.overallRating
              : Number(response.overallRating);
            const mappedRating = res.ratingMap[overallRating];
            return { ...response, numOverallRating: mappedRating || overallRating };
          });
          setPerformanceReviewData((prevPerformanceReviewData) => ({
            ...prevPerformanceReviewData,
            ratingMap: flippedRatingMap,
            reviewRatingDetailsResponseList: newReviewRatingDetailsResponseList,
            color: res.color,
          }));
        })
        .catch((err) => console.error('Error fetching data for performance review :', err))
        .finally(() => {
          setLoading(false);
        });
    };

    fetchPerformanceReviewData();
  }, [loggedInEmployeeId]);

  return (
    <>
      <SectionHeader icon={<ReviewChartMiniIcon />} title="Performance" withInComponent={false} />
      <StyledPaper
        padding="2rem"
        paddingTop="1rem"
        paddingBottom="1rem"
        style={{
          height: '40vh',
          maxHeight: '500px',
          minHeight: '300px',
          position: 'relative',
        }}
      >
        {loading && <Loader />}
        {!loading &&
          (performanceReviewData.reviewRatingDetailsResponseList.length !== 0 ? (
            <MeshScatterChart
              name="Performance Review Graph"
              data={performanceReviewData.reviewRatingDetailsResponseList}
              xAxisAttributes={{
                dataKey: 'reviewCycle',
                name: 'Review Cycle',
                label: 'Review Cycles',
              }}
              yAxisAttributes={{
                dataKey: 'numOverallRating',
                name: 'Performance',
                type: 'number',
                domain: [0, Object.keys(performanceReviewData.ratingMap).length],
                ticks: Object.keys(performanceReviewData.ratingMap),
                tickFormatter: (value) =>
                  Number.isNaN(performanceReviewData.ratingMap[value])
                    ? performanceReviewData.ratingMap[value]
                    : Number(performanceReviewData.ratingMap[value]),
                label: 'Rating',
              }}
              fillColor={performanceReviewData.color}
              metaData="ratingSplit"
              metaDataMap={performanceReviewData.dataMap}
              metaDataColorMap={performanceReviewData.colorMap}
            />
          ) : (
            <PerformanceReviewGraphPlaceholder />
          ))}
      </StyledPaper>
    </>
  );
};

export default PerformanceReviewGraph;
